<template lang="pug">
.gym-filters(:class="{collapsed: collapseFilters}" )
  button.collapse-toggle(type="button" @click="toggleMobileFilters")
  h3.filter-title {{ __('Rogue Gyms in your Area') }}
  a.faq-info(@click="showFaq()" v-if="roguebuilder")
    Icon(name="info" fill="gray")
  roguebuilder-sidebar.faq-sidebar(:entity="{roguebuilder: roguebuilder}" ref="dynamicSidebar")
  .search-field
    RogueInput.gym-search-box(
      :placeholder="__('Search by location, Rogue product, or gym...')"
      icon="search"
      v-model="searchText"
      @update:modelValue="searchTextChanged($event)"
      @keydown.enter="submitSearch()"
      @keydown.down="highlightNextSuggestion()"
      @keydown.up="highlightPreviousSuggestion()"
      :hasClearButton="true"
    )
    .suggestions(v-if="suggestions.length")
      .suggestion(
        v-for="(suggestion, i) in suggestions"
        @click="emit('search', suggestion)"
        :class="{highlighted: highlightedSuggestion !== null && i === highlightedSuggestion}"
      )
        .type {{ __(suggestion.type) }}
        .text {{ __(suggestion.text) }}
        .secondary-text(v-if="suggestion.secondaryText") {{ __(suggestion.secondaryText) }}
  .dropdown-filters
    RogueSelect(
      :options="gymTypeOptions"
      :modelValue="selectedGymType"
      @update:modelValue="$emit('updateGymType', $event)"
      color="flat"
    )
  .filter-toggle
    label.filter-label(for="featured-gym-toggle")
      | {{ __('Rogue Featured Gyms Only') }}
    ToggleSwitch(
      id="featured-gym-toggle"
      :modelValue="featuredOnly"
      @update:modelValue="$emit('toggleFeatured', !featuredOnly)"
    )
  .filter-toggle
    label.filter-label(for="dropin-gym-toggle")
      | {{ __('Drops-Ins Only') }}
    ToggleSwitch(
      id="dropin-gym-toggle"
      :modelValue="dropInsOnly"
      @update:modelValue="$emit('toggleDropIns', !dropInsOnly)"
    )
</template>
<script setup lang="ts">
import type { IGym } from '@rogue/universal'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { widgets } from '@/js/services/rfk/types'
import { SECTIONS } from '@/js/services/cnstrc/constants'
import { useCmsStore } from '@/js/stores/cmsStore'

import { useRogue } from '@/js/services/Rogue'
import RoguebuilderSidebar from '@/js/components/roguebuilder-sidebar.vue'

import type { Suggestion } from './gymTypes'
import { GYM_TYPES } from './gymConstants'

const rogue = useRogue()
const route = useRoute()
const cmsStore = useCmsStore()

const props = defineProps<{
  useMiles: boolean,
  activeSearchText: string,
  selectedGymType: string,
  featuredOnly: boolean,
  dropInsOnly: boolean,
  gyms:(IGym & { formattedAddress?: string, _id?: string })[],
  sessionToken: string,
  collapseFilters: boolean,
}>()

const searchText = ref(props.activeSearchText)

const emit = defineEmits<{(e: 'search', value: string | Suggestion): void,
  (e: 'updateGymType', type: string): void,
  (e: 'toggleFeatured', featuredOnly: boolean): void,
  (e: 'toggleDropIns', dropInsOnly: boolean): void,
  (e: 'toggleCollapse'): void
}>()

const gymTypes = ['All Rogue Gyms'].concat(GYM_TYPES)
const gymTypeOptions = gymTypes.map(type => ({ label: type, value: type }))

const suggestions = ref<Suggestion[]>([])
const highlightedSuggestion = ref<number | null>(null)

const dynamicSidebar = ref<typeof RoguebuilderSidebar>()
const GYM_MAP_FAQ_ID = 'gym-map-faq'

const roguebuilder = computed(() => cmsStore.cms[cmsStore.byIdentifier[GYM_MAP_FAQ_ID]]?.roguebuilder)

async function showFaq() {
  dynamicSidebar.value?.show('gymfaq')
}

async function getLocationSuggestions(query: string): Promise<Suggestion[]> {
  const accessToken = rogue.keys.mapbox?.searchBoxApi
  const limit = 2
  // Documentation for types returned by mapbox: https://docs.mapbox.com/api/search/search-box/
  const resp = await rogue.fetch<any>(`https://api.mapbox.com/search/searchbox/v1/suggest?q=${query}&access_token=${accessToken}&session_token=${props.sessionToken}&language=${rogue.l10n.lang}&limit=${limit}&types=country,region,postcode,district,place,city,locality,neighborhood,street,address`, 'get')
  if (resp.suggestions?.length) {
    return resp.suggestions.map((s: any) => ({
      type          : 'Location',
      text          : s.name,
      secondaryText : s.full_address || s.place_formatted,
      uniqueId      : s.mapbox_id,
    }))
  }

  return []
}

async function getProductSuggestions(query: string): Promise<Suggestion[]> {
  const useConstructor = rogue.featureFlags?.isOn('enable_constructor')

  const request = useConstructor
    ? rogue.cnstrc.getAutocompleteResults(query, 2, SECTIONS.PRODUCTS)
    : rogue.rfk?.fetch({ widget: widgets.SEARCH_PREVIEW, uri: route.path, n_item: 2, query }, 0)

  const resp = await request
  if (resp?.content?.product?.value?.length) {
    return resp.content.product.value.map((p: any) => ({
      type     : 'Rogue Product',
      text     : p.name,
      uniqueId : p.productId,
    }))
  }

  return []
}

async function updateSuggestions(query: string) {
  if (!query) {
    suggestions.value = []
    highlightedSuggestion.value = null

    return
  }

  const lowerCase = query.toLowerCase()

  const includedGyms = props.gyms
    .filter(gym => !props.featuredOnly || (props.featuredOnly && gym.isFeatured))
    .filter(gym => !props.dropInsOnly || (props.dropInsOnly && gym.allowDropIn))
    .filter(gym => props.selectedGymType === 'All Rogue Gyms' || props.selectedGymType === gym.type)
  const gymSuggestions: Suggestion[] = includedGyms
    .filter(gym => gym.name.toLowerCase().includes(lowerCase))
    .slice(0, 2)
    .map(gym => ({
      type          : 'Gym',
      text          : gym.name,
      secondaryText : gym.shareAddress ? gym.formattedAddress : undefined,
      uniqueId      : gym._id,
    }))

  let locationSuggestions: Suggestion[] = []
  let productSuggestions: Suggestion[] = []

  const locationPromise = getLocationSuggestions(query).then(results => { locationSuggestions = results })
  const productPromise = getProductSuggestions(query).then(results => { productSuggestions = results })

  await Promise.all([locationPromise, productPromise])

  suggestions.value = locationSuggestions.concat(productSuggestions, gymSuggestions)
  highlightedSuggestion.value = null
}

let suggestionTimeout: any = null
function searchTextChanged(query: string) {
  if (!query) {
    emit('search', '')
  }
  clearTimeout(suggestionTimeout)
  suggestionTimeout = setTimeout(() => updateSuggestions(query), 500)
}

function highlightPreviousSuggestion() {
  if (suggestions.value.length) {
    if (highlightedSuggestion.value === null) {
      highlightedSuggestion.value = (suggestions.value.length - 1)
    } else if (highlightedSuggestion.value === 0) {
      highlightedSuggestion.value = null
    } else {
      highlightedSuggestion.value -= 1
    }
  }
}

function highlightNextSuggestion() {
  if (suggestions.value.length) {
    if (highlightedSuggestion.value === null) {
      highlightedSuggestion.value = 0
    } else if (highlightedSuggestion.value === suggestions.value.length - 1) {
      highlightedSuggestion.value = null
    } else {
      highlightedSuggestion.value += 1
    }
  }
}

function submitSearch() {
  // @ts-ignore
  if (document?.activeElement?.blur) {
    // @ts-ignore
    document.activeElement.blur()
  }
  if (highlightedSuggestion.value !== null) {
    emit('search', suggestions.value[highlightedSuggestion.value])
  } else {
    emit('search', searchText.value)
  }
}

const collapseResults = ref(false)
function toggleMobileFilters() {
  emit('toggleCollapse')
}

watch(() => props.activeSearchText, () => {
  clearTimeout(suggestionTimeout)
  searchText.value = props.activeSearchText
  updateSuggestions(props.activeSearchText)
})

onMounted(async () => {
  await cmsStore.load(GYM_MAP_FAQ_ID, rogue)
})

</script>
<style lang="scss" scoped>
.gym-filters {
  background-color: $color-white;
  border-bottom: 1px solid $color-gray;
  display: flex;
  flex-direction: column;
  gap: $space-s;
  padding: $space-s;
  position: relative;
  outline: none;

  .faq-info {
    right: $space-s;
    position: absolute;
    top: $space-s;

    @include not-desktop() {
      top: 0;
    }
  }

  .faq-sidebar {
    position: absolute;
  }

  .collapse-toggle {
    display: none;
    position: absolute;
    top: -26px;
    left: 0;
    right: 0;
    height: 26px;
    z-index: 1;
    box-shadow: 0px -4px 4px 0px rgba(0, 0, 0, 0.5);
    border: none;
    border-radius: $space-xxs $space-xxs 0px 0px;
    background: $color-white;
    padding: 0;

    &::before {
      background: $color-gray;
      height: 4px;
      width: 32px;
      border-radius: 2px;
      content: ' ';
      display: block;
      margin: 12px auto;
    }

    @include not-desktop() {
      display: block;
    }
  }

  .filter-title {
    margin: 0;
    width: 100%;
  }

  .search-field {
    position: relative;
    z-index: 2;

    .gym-search-box {
      margin: 0;
    }

    .suggestions {
      background: $color-gray-x-dark;
      border: 1px solid $color-black;
      display: none;
      flex-direction: column;
      gap: 2px;
      padding: $space-xxxs;
      position: absolute;
      width: 100%;

      .suggestion {
        display: flex;
        flex-direction: column;
        gap: $space-xxxs;
        padding: $space-xxxs;

        .type {
          color: $color-gray-x-light;
          font-size: 10px;
          font-weight: bold;
          line-height: 12px;
          text-transform: uppercase;
        }

        .text {
          color: $color-white;
          font-size: $size-xs;
          line-height: $size-xs;
        }

        .secondary-text {
          color: $color-gray-light;
          font-size: $size-xxs;
          line-height: $size-xs;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        &:hover,
        &.highlighted {
          background-color: #4599ff;
          cursor: pointer;

          .type {
            color: $color-gray;
          }
        }
      }
    }

    &:focus-within,
    &:active,
    &:focus {
      .suggestions {
        display: flex;
      }
    }

    @include not-desktop() {
      position: fixed;
      // Height of the mobile header + padding
      top: calc(51px + $space-xs);
      left: $space-s;
      right: $space-s;
    }
  }

  .dropdown-filters {
    display: flex;
    flex-direction: row;
    gap: $space-s;
    width: 100%;

    .select {
      flex: 1;
      margin: 0;
    }
  }

  .filter-toggle {
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: $space-s;

    .filter-label {
      flex-grow: 1;
      font-family: $font-header;
      font-size: $size-xs;
      font-weight: bold;
      margin: 0;
      text-transform: uppercase;
    }
  }

  @include not-desktop() {
    padding-top: 0;

    &.collapsed {
      .dropdown-filters, .filter-toggle {
        display: none;
      }
    }
  }
}
</style>

<style lang="scss" scoped target="app">
.platform-ios,
.platform-android {
  .search-field {
    @include not-desktop() {
      top: $space-xs;
    }
  }
}
</style>
